import { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { ImageCarousel } from '@spotahome/ui-library';

import { getModifier } from './utils/getSliderComponent';
import { IMG_TYPE } from './utils/constants';

import SliderTrack from './SliderTrack';
import SingleSliderTrack from './SingleSliderTrack';
import CarouselItemsWrapper from './CarouselItemsWrapper';

import './SliderGallery.scss';

const dimensionPropType = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.number
]);

const TRACK_TYPE_CORRELATIONS = {
  // eslint-disable-next-line react/prop-types
  [IMG_TYPE]: ({ items, ref }) => (
    <ImageCarousel
      items={items}
      chevronStyle="big"
      ratio={ImageCarousel.RATIOS.PANORAMIC}
      keyboardHandleEnabled
      showCounter
      showCaption
      ref={ref}
      preloadRange={2}
      autoFocus
    />
  ),
  // eslint-disable-next-line react/prop-types
  default: ({ items, type, ref, currentTrackItem }) => (
    <SliderTrack
      ref={ref}
      type={type}
      items={items}
      currentTrackItem={currentTrackItem}
    />
  )
};

const SliderGallery = props => {
  const [currentTrack, setCurrentTrack] = useState(props.defaultTrackToShow);
  const selectedTrack = props.tracks[currentTrack];

  useEffect(() => {
    props.onTrackChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTrack]);

  const galleryClassName = classNames('slider-gallery', {
    'slider-gallery--is-vertical': props.isLandscape
  });

  const getGalleryStyles = () => {
    const { type } = props.tracks[currentTrack];
    const galleryModifier = getModifier(type);
    const galleryStyles = {
      height: props.height,
      width: props.width
    };

    return { ...galleryStyles, ...galleryModifier };
  };

  const handleTrackSelection = i => {
    setCurrentTrack(i);
  };

  const renderTrackItem =
    TRACK_TYPE_CORRELATIONS[selectedTrack.type] ||
    TRACK_TYPE_CORRELATIONS.default;

  return (
    <Fragment>
      <div className={galleryClassName} style={getGalleryStyles()}>
        {selectedTrack.items.length > 1 ? (
          renderTrackItem({
            items: selectedTrack.items,
            type: selectedTrack.type,
            ref: props.counterRef,
            currentTrackItem: props.currentTrackItem
          })
        ) : (
          <SingleSliderTrack
            type={selectedTrack.type}
            item={{
              ...selectedTrack.items[0],
              listingId: props.listingId
            }}
          />
        )}
      </div>
      <CarouselItemsWrapper
        carouselRef={props.carouselRef}
        onClick={handleTrackSelection}
        trackData={props.tracks}
        isFullScreen
        isVertical={props.isLandscape}
        selectedTrack={currentTrack}
        carouselHeight={props.height}
        carouselWidth={props.width}
      />
    </Fragment>
  );
};

SliderGallery.propTypes = {
  height: dimensionPropType,
  width: dimensionPropType,
  isLandscape: PropTypes.bool,
  tracks: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  defaultTrackToShow: PropTypes.number.isRequired,
  onTrackChange: PropTypes.func.isRequired,
  counterRef: PropTypes.shape({}).isRequired,
  carouselRef: PropTypes.shape({}).isRequired,
  currentTrackItem: PropTypes.number.isRequired,
  listingId: PropTypes.string.isRequired
};

SliderGallery.defaultProps = {
  height: 'auto',
  width: 'auto',
  isLandscape: false
};

export default SliderGallery;
